import { Suspense, useMemo, useEffect } from 'react';
import { Outlet, useMatch, useNavigate } from 'react-router-dom';
import classNames from 'classnames';

import { DashboardPage } from '/src/components/Page';
import { DashHeader } from '/src/components/headers/DashHeader';
import { SEO } from '/src/components/SEO';
import { usePermissions, useSite } from '/src/hooks';
import { FEATURES, ALL } from '/src/features/_global/constants';
import { SITE_SETTINGS_TAB } from '/src/features/options/constants';

import { Button } from '@crazyegginc/hatch';

import { SitePickerBar } from '/src/components/site-picker/index';

import { ReactComponent as ExpandIcon } from '@crazyegginc/hatch/dist/images/icon-arrow-expand.svg';
import { ReactComponent as ArrowIcon } from '@crazyegginc/hatch/dist/images/icon-arrow.svg';

// eslint-disable-next-line no-restricted-syntax
export default function OptionsPage() {
  const permissions = usePermissions();

  const siteSettingsMatch = useMatch('/options/site-settings/*');
  const isInSiteDetail = siteSettingsMatch && siteSettingsMatch.params['*']?.includes('/');

  const { sites, loadingSites, selectSite } = useSite({
    enableAllSites: true,
  });

  const canEditSites = permissions.can('create', FEATURES.SITES).allowed;

  const pageTitle = isInSiteDetail ? 'Options > Site Settings' : 'Options';

  const navigate = useNavigate();

  const remainder = siteSettingsMatch?.params?.['*'] ?? '';
  const [maybeSiteId, currentTab] = remainder.split('/');

  const getTabToPreserve = () => {
    if (isInSiteDetail && currentTab) return currentTab;

    return SITE_SETTINGS_TAB.RECORDINGS.path;
  };

  const selectedSite = useMemo(() => {
    if (!maybeSiteId) return ALL;
    if (!isInSiteDetail && (!sites || sites.length <= 1)) return ALL;
    const id = Number(maybeSiteId);
    if (!Number.isFinite(id)) return ALL;
    return sites?.find((s) => s.id === id) ?? ALL;
  }, [maybeSiteId, sites, isInSiteDetail]);

  useEffect(() => {
    if (!isInSiteDetail) return;

    const id = Number(maybeSiteId);
    if (!Number.isFinite(id)) return;
    if (!sites || sites.length === 0) return;

    const siteFromUrl = sites.find((s) => s.id === id);
    if (!siteFromUrl) return;

    window.localStorage.removeItem('shell:allsites');
    selectSite(siteFromUrl, true);
  }, [isInSiteDetail, maybeSiteId, sites, selectSite]);

  const handleSelectSite = (site) => {
    if (site === ALL) {
      selectSite(ALL);
      navigate('/options/site-settings');
      return;
    }

    window.localStorage.removeItem('shell:allsites');
    selectSite(site, true);
    const tab = getTabToPreserve();
    navigate(`/options/site-settings/${site.id}/${tab}`);
  };

  const handleBackToAllSites = () => {
    selectSite(ALL);
    navigate('/options/site-settings');
  };

  useEffect(() => {
    if (!isInSiteDetail) {
      selectSite(ALL);
    } else {
      window.localStorage.removeItem('shell:allsites');
    }
  }, [isInSiteDetail, selectSite]);

  return (
    <DashboardPage>
      <SEO title={pageTitle} />
      <DashHeader
        titleComponent={
          isInSiteDetail ? (
            <div className="flex min-w-0 items-center">
              <button
                onClick={handleBackToAllSites}
                className={classNames('min-w-0 shrink-0 hover:underline', {
                  'text-lynch-500': isInSiteDetail,
                })}
              >
                Options
              </button>
              <ExpandIcon className="mx-2.5 h-3 w-3 rotate-90 fill-current text-cadet-blue-500" />
              Site Settings
            </div>
          ) : (
            'Options'
          )
        }
        actionButton={
          isInSiteDetail ? (
            <Button variant="secondary" onClick={handleBackToAllSites} size="xl" className="text-center leading-none">
              <ArrowIcon className="mr-2 h-3 w-3 shrink-0 rotate-180 fill-current" />
              Back to All Sites
            </Button>
          ) : null
        }
      />
      {siteSettingsMatch ? (
        <SitePickerBar
          sites={sites}
          selectedSite={selectedSite}
          selectSite={handleSelectSite}
          loading={loadingSites}
          addSiteOption={canEditSites && isInSiteDetail}
          hidePageFeedback
          pageDisplayName="Site Settings"
        />
      ) : null}

      <div className={classNames({ 'mx-10': !isInSiteDetail, 'mt-10': !isInSiteDetail })}>
        <Suspense fallback={null}>
          <Outlet />
        </Suspense>
      </div>
    </DashboardPage>
  );
}
